import { previousSeason, type Rankings as RankingsData } from '@drawgon/shared';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CharacterCard } from '../components/CharacterCard';
import { fetchRankings } from '../lib/api';
import { alert, alertTitle, heading, lead, panel } from '../lib/ui';

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; rankings: RankingsData };

const textLink = 'text-text underline decoration-2 underline-offset-4';

/**
 * ランキング（企画書 §17.2, §26）。
 *
 * 大会の結果を確認しに戻ってくる導線の受け皿。ログイン不要で見られることが重要で、
 * 「あなたのキャラが BEST 8 に進出しました」という通知からここへ着地する想定。
 *
 * 並びは**大会の順位ポイントの合計の降順**（ADR-012、配点は ADR-023）。順位はサーバーが付ける
 * （同点は同じ順位）。配列の添字を順位にすると、**最初は全員 0 点**なので嘘になる。
 *
 * **シーズンは日本時間の暦の 1 か月**（#284、ADR-013）。`/rankings` は今のシーズン、`/rankings?season=YYYY-MM` は
 * その月。今のシーズンのページから、せんげつへのリンクを置く（1 日には今のシーズンが空になるので）。
 */
export function Rankings() {
  const [params] = useSearchParams();
  const season = params.get('season') ?? undefined;
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    fetchRankings(season)
      .then((rankings) => {
        if (!cancelled) setState({ status: 'ready', rankings });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'データを取得できませんでした',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [season]);

  // 今のシーズンを見ているか。**指定が無いときだけ**（今の月を決めるのはサーバー。ブラウザの時計で比べない）。
  const isCurrent = season === undefined;

  return (
    <div className="space-y-6 pt-4">
      <header>
        <h1 className={heading}>ランキング</h1>
        {state.status === 'ready' && (
          <p className="mt-1 font-display text-lg">{seasonLabel(state.rankings.season.id)}</p>
        )}
        <p className={`${lead} mt-2 text-sm`}>
          その月のワールドカップでためたポイントの多い順。毎月 1
          日（日本時間）に新しいシーズンが始まります。
        </p>
      </header>

      {state.status === 'loading' && (
        <div className={`${panel} text-sm text-text-muted`}>よみこみ中…</div>
      )}

      {state.status === 'error' && (
        <div role="alert" className={alert}>
          <p className={alertTitle}>よみこめませんでした</p>
          <p className="mt-1 text-sm">{state.message}</p>
        </div>
      )}

      {state.status === 'ready' && (
        <>
          <nav aria-label="シーズン" className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link
              to={`/rankings?season=${previousSeason(state.rankings.season.id)}`}
              className={textLink}
            >
              ← {isCurrent ? 'せんげつ' : seasonLabel(previousSeason(state.rankings.season.id))}
            </Link>
            {!isCurrent && (
              <Link to="/rankings" className={textLink}>
                こんげつ の ランキング →
              </Link>
            )}
          </nav>

          {state.rankings.entries.length === 0 ? (
            <div className={`${panel} text-sm text-text-muted`}>
              {isCurrent
                ? 'このシーズンは、まだ だれも ワールドカップに出ていません。'
                : 'このシーズンは、だれも ワールドカップに出ていませんでした。'}
            </div>
          ) : (
            <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {state.rankings.entries.map((entry) => (
                <li key={entry.character.id}>
                  <CharacterCard
                    character={entry.character}
                    rank={entry.rank}
                    points={entry.points}
                  />
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </div>
  );
}

/** シーズンの呼び方（#284）。`2026-10` → 「10がつ の シーズン」（アプリと同じ）。 */
function seasonLabel(id: string): string {
  return `${Number(id.slice(5))}がつ の シーズン`;
}
